import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { actionEncargosGet } from "../../redux/actions/encargos/encargos";

import { Button, Card, Input, Space, Typography, Row, Col, Skeleton, ConfigProvider } from "antd";
import { SearchOutlined } from "@ant-design/icons";

import { showHerramientasEffect } from "../../components/utils/antWaveEffect";
import "./PaquetesPage.css";

const { Title, Text, Paragraph } = Typography;

const BANNER_BG = "linear-gradient(135deg, #3d0011 0%, #5c0019 100%)";
const ESTADO_ENTREGADO = 3;
const ESTADO_CANCELADO = 4;
const ESTADO_DISPONIBLE = 6;
const ESTADO_AVISADO = 2;

export default function EncargosPage() {
  const dispatch = useDispatch();
  const { items = [], loading } = useSelector((state) => state.encargos);
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [activeFilter, setActiveFilter] = useState("todos");

  const lastFetchKey = useRef("");

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const fetchParams = useMemo(() => ({ search: searchDebounced || undefined }), [searchDebounced]);

  useEffect(() => {
    const key = JSON.stringify(fetchParams);
    if (lastFetchKey.current === key) return;
    lastFetchKey.current = key;
    dispatch(actionEncargosGet(fetchParams));
  }, [dispatch, fetchParams]);

  const counts = useMemo(() => ({
    total: items.length,
    encargos: items.filter((e) => e.tipo === "encargo").length,
    apartados: items.filter((e) => e.tipo === "apartado").length,
    listos: items.filter((e) => [ESTADO_DISPONIBLE, ESTADO_AVISADO].includes(e.id_estado_encargo)).length,
  }), [items]);

  const filteredItems = useMemo(() => {
    if (activeFilter === "encargos") return items.filter((e) => e.tipo === "encargo");
    if (activeFilter === "apartados") return items.filter((e) => e.tipo === "apartado");
    if (activeFilter === "listos") return items.filter((e) => [ESTADO_DISPONIBLE, ESTADO_AVISADO].includes(e.id_estado_encargo));
    return items;
  }, [items, activeFilter]);

  const STAT_CARDS = [
    { key: "todos", label: "Total", icon: <i className="fa-solid fa-box-archive" />, iconCls: "paq-stat-icon-sonido", count: counts.total },
    { key: "encargos", label: "Encargos", icon: <i className="fa-solid fa-truck-field" />, iconCls: "paq-stat-icon-barra", count: counts.encargos },
    { key: "apartados", label: "Apartados", icon: <i className="fa-solid fa-tag" />, iconCls: "paq-stat-icon-receta", count: counts.apartados },
    { key: "listos", label: "Listos para entrega", icon: <i className="fa-solid fa-circle-check" />, iconCls: "paq-stat-icon-decoracion", count: counts.listos },
  ];

  const ESTADO_STRIP = (e) => {
    if (e.id_estado_encargo === ESTADO_CANCELADO) return "is-red";
    if (e.id_estado_encargo === ESTADO_ENTREGADO) return "is-green";
    if ([ESTADO_DISPONIBLE, ESTADO_AVISADO].includes(e.id_estado_encargo)) return "is-green";
    return "is-red";
  };

  return (
    <ConfigProvider wave={{ showEffect: showHerramientasEffect }}>
      <main className="paq-main">
        <div className="paq-content">
          <section className="paq-section">
            <div className="paq-filters-panel">
              <Space direction="vertical" size={2} style={{ marginBottom: 16 }}>
                <Title level={2} className="paq-title">Encargos y Apartados</Title>
                <Text className="paq-subtitle">Productos pedidos o reservados para clientes.</Text>
              </Space>

              <Row gutter={[16, 14]}>
                <Col xs={24}>
                  <div className="paq-field-label">Buscador</div>
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar por cliente o descripcion..."
                    suffix={<SearchOutlined className="paq-input-suffix" />}
                    className="paq-control"
                    allowClear
                  />
                </Col>
              </Row>
            </div>

            <div className="paq-stats-row" style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))" }}>
              {STAT_CARDS.map(({ key, label, icon, iconCls, count }) => (
                <Card
                  key={key}
                  className={`paq-stat-card ${activeFilter === key ? "paq-stat-active" : ""}`}
                  hoverable
                  onClick={() => setActiveFilter((p) => (p === key ? "todos" : key))}
                >
                  <Space align="center" size={10}>
                    <div className={`paq-stat-icon ${iconCls}`}>{icon}</div>
                    <div>
                      <div className="paq-stat-value">{count}</div>
                      <div className="paq-stat-label">{label}</div>
                    </div>
                  </Space>
                </Card>
              ))}
            </div>

            <div className="paq-expedientes-card">
              <div className="paq-toolbar">
                <div className="paq-toolbar-left">
                  <Title level={4} style={{ marginBottom: 0 }}>Registros ({filteredItems.length})</Title>
                  <Text type="secondary">{filteredItems.length} encontrados</Text>
                </div>
                <div className="paq-toolbar-right">
                  <>
                    <Button
                      type="primary"
                      icon={<i className="fa-solid fa-plus" />}
                      onClick={() => navigate("/app/encargos/crear")}
                      style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}
                    >
                      Nuevo encargo/apartado
                    </Button>
                  </>
                </div>
              </div>

              {!loading && filteredItems.length === 0 && (
                <div className="paq-empty">
                  <div style={{ fontSize: 16, fontWeight: 500 }}>Sin registros que coincidan con los filtros</div>
                </div>
              )}

              <div className="paq-category-block">
                <div className="paq-grid">
                  {loading && Array.from({ length: 4 }).map((_, i) => (
                    <div key={`sk-${i}`} className="paq-card paq-card-skeleton">
                      <div className="paq-card-header">
                        <Skeleton active title={{ width: "60%" }} paragraph={{ rows: 1, width: "40%" }} />
                      </div>
                      <div className="paq-card-body">
                        <Skeleton active title={false} paragraph={{ rows: 3 }} />
                      </div>
                    </div>
                  ))}

                  {!loading && filteredItems.length > 0 && (
                    <>
                      {filteredItems.map((e) => (
                        <div key={e.id_encargo} className="paq-card">
                          <div className="paq-card-banner" style={{ background: BANNER_BG }}>
                            <i className={`fa-solid ${e.tipo === "apartado" ? "fa-tag" : "fa-truck-field"} paq-card-banner-icon`} />
                          </div>

                          <div className="paq-card-header">
                            <div className="paq-card-head">
                              <Paragraph className="paq-card-name" ellipsis={{ rows: 2 }}>{e.descripcion}</Paragraph>
                              <span className="paq-tipo-badge paq-tipo-sonido" style={{ textTransform: "capitalize" }}>{e.tipo}</span>
                            </div>
                          </div>

                          <div className="paq-card-body">
                            <div className="paq-contenidos-box">
                              <i className="fa-solid fa-user paq-info-icon" />
                              <div className="paq-info-text">
                                <span className="paq-info-label">Cliente</span>
                                <span className="paq-info-value">{e.cliente_nombre}</span>
                              </div>
                            </div>
                            {e.proveedor_nombre && (
                              <div className="paq-contenidos-box">
                                <i className="fa-solid fa-truck-field paq-info-icon" />
                                <div className="paq-info-text">
                                  <span className="paq-info-label">Proveedor</span>
                                  <span className="paq-info-value">{e.proveedor_nombre}</span>
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="paq-card-footer">
                            <div className={`paq-bottom-strip ${ESTADO_STRIP(e)}`}>
                              <span className="paq-strip-label">{e.estado_nombre}</span>
                            </div>
                            <Button
                              className="paq-btn-details"
                              icon={<i className="fa-solid fa-circle-info" />}
                              onClick={() => navigate(`/app/encargos/${e.id_encargo}`)}
                            >
                              Ver detalles
                            </Button>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </ConfigProvider>
  );
}
